import CatalogSkeleton from 'components/CatalogSkeleton'
import VideoList from 'components/VideoList'
import { CoreContext } from 'context/CoreContext'
import React, { useEffect, useState, useContext, useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { FormSpacer, Icon } from 'ui/styled'
import useI18n from 'hooks/useI18n'
import useList from 'hooks/useList'
import { adapters, resolveLocalizedTitle } from 'utils/adapters'
import CustomTablePagination from 'components/CustomTablePagination'
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min'
import { SaveObject } from 'services/storage'
import { Read as readFavorites, Toggle as toggleFavorite } from 'services/favorites'
import { exposeStrapiError, isModularContentType, normalizeStrapiRegister } from 'utils'
import { languages } from 'utils/options'
import { PaginationContainer } from './styled'
import { useAuthLayout } from 'context/AuthLayoutContext'
import { isSupportVideosCategory } from 'utils/supportVideos'
import { Read } from 'services/core'

const DEFAULT_RENDER_OPTION = 'gallery'
const FAVORITABLE_TYPES = new Set([
  'documents',
  'support-materials',
  'marketing-materials',
  'sales-support-materials',
  'service-supports',
  'media-templates',
  'product-informations',
])

const normalizeRawCategories = (item) => {
  if (!item?.raw_category) return []
  return Array.isArray(item.raw_category) ? item.raw_category : [item.raw_category]
}

const normalizeSubCategories = (item) => {
  if (!item?.sub_categories) return []
  return Array.isArray(item.sub_categories) ? item.sub_categories : [item.sub_categories]
}

export default function SubCategory() {
  const { id, type } = useParams()
  const location = useLocation()
  const params = new URLSearchParams(location?.search)
  const subId = params.get('q')

  const { t } = useI18n()
  const { setFiltered, language } = useContext(CoreContext)
  const locale = useMemo(() => languages.find((item) => item.id === language)?.ref || 'en', [language])

  const { setIsActive, setLayoutProps } = useAuthLayout()

  const [renderOption, setRenderOption] = useState(DEFAULT_RENDER_OPTION)
  const [favoriteLoadingId, setFavoriteLoadingId] = useState(null)
  const [favoriteDocumentMap, setFavoriteDocumentMap] = useState({})
  const [routeCategory, setRouteCategory] = useState(null)
  const pageSize = 50
  const favoritesEnabled = FAVORITABLE_TYPES.has(type)
  const isModularType = isModularContentType(type)
  const supportVideosCategory = type === 'service-supports' && isSupportVideosCategory(routeCategory)

  useEffect(() => {
    setLayoutProps({})
  }, [setLayoutProps])

  useEffect(() => {
    setRouteCategory(null)
  }, [id, type])

  const filters = useMemo(() => {
    const parts = []

    if (id) parts.push(`filters[categories][id]=${id}`)
    if (subId) parts.push(`filters[sub_categories][id][$in]=${subId}`)
    return parts.join('&')
  }, [id, subId])

  const { registers, loading, error, retry, pageCount, total, setPage, page } = useList({
    table: type,
    adapter: adapters?.[type],
    categoryFilter: filters,
    sortQuery: supportVideosCategory ? 'sort[0]=order:asc&sort[1]=title:asc' : null,
  })

  useEffect(() => {
    if (type !== 'service-supports' || !id) {
      setRouteCategory(null)
      return
    }
    if (loading) return

    const populatedCategory = (registers || [])
      .flatMap(item => normalizeRawCategories(item))
      .find(category => `${category?.id}` === `${id}`)

    if (populatedCategory) {
      setRouteCategory(populatedCategory)
      return
    }

    let active = true
    Read('categories', `/${id}?locale=${encodeURIComponent(locale)}&populate[title_translations]=true`)
      .then(result => {
        if (!active || result?.error) return
        const category = normalizeStrapiRegister(result) || result?.data || null
        setRouteCategory(!category ? null : {
          ...category,
          sourceTitle: category?.title,
          title: resolveLocalizedTitle(category, locale),
        })
      })
      .catch(() => {})

    return () => {
      active = false
    }
  }, [id, loading, locale, registers, type])

  useEffect(() => {
    setFiltered(null)
    SaveObject('filtered', null)
  }, [location, setFiltered])

  useEffect(() => {
    let active = true

    const loadFavorites = async () => {
      if (!favoritesEnabled) {
        setFavoriteDocumentMap({})
        return
      }

      const result = await readFavorites(locale)
      if (!active || exposeStrapiError(result)) return

      const nextMap = (result?.data || []).reduce((acc, favorite) => {
        if (!favorite?.uid || !favorite?.documentId) return acc
        acc[`${favorite.uid}:${favorite.documentId}`] = favorite.id
        return acc
      }, {})

      setFavoriteDocumentMap(nextMap)
    }

    loadFavorites()

    return () => {
      active = false
    }
  }, [favoritesEnabled, locale])

  const courseViewModel = useMemo(() => {
    if (!isModularType) {
      return {
        activeCategory: null,
        items: [],
      }
    }

    const items = Array.isArray(registers) ? [...registers] : []
    const categoryEntries = items.flatMap((item) => normalizeRawCategories(item))

    const activeCategory =
      categoryEntries.find((category) => `${category?.id}` === `${id}`) ||
      categoryEntries[0] ||
      null

    const selectedItems = items.sort((a, b) => {
      if (!a?.order) return -b?.order
      if (!b?.order) return -a?.order
      return (a?.order || 0) - (b?.order || 0)
    })

    return {
      activeCategory,
      items: selectedItems,
    }
  }, [id, isModularType, registers])

  const categories = useMemo(() => {
    if (isModularType) {
      const selectedCategoryTitle = courseViewModel?.activeCategory?.title || ''
      const selectedSubCategoryTitle = subId
        ? (courseViewModel?.items || [])
          .flatMap((item) => normalizeSubCategories(item))
          .find((subCategory) => `${subCategory?.id}` === `${subId}`)?.title || ''
        : ''
      const titleParts = [t(type), selectedCategoryTitle, selectedSubCategoryTitle].filter(Boolean)
      const rootPath = `/sub-category/${type}`
      const categoryPath = id ? `/sub-category/${id}/${type}` : rootPath
      const breadcrumbs = [
        { label: t('home'), icon: 'home', to: '/dashboard' },
        { label: t(type), to: selectedCategoryTitle ? rootPath : null, primary: true },
        selectedCategoryTitle ? { label: selectedCategoryTitle, to: selectedSubCategoryTitle ? categoryPath : null } : null,
        selectedSubCategoryTitle ? { label: selectedSubCategoryTitle, to: null } : null,
      ].filter(Boolean)

      return [
        {
          section: t(`${type}_available_for_you`),
          title: titleParts.length > 1 ? `${titleParts[0]} <small>> ${titleParts.slice(1).join(' > ')}</small>` : titleParts[0],
          breadcrumbs,
          data: courseViewModel?.items || [],
          type,
        }
      ]
    }

    const items = registers || []

    const matchesId = (item) => {
      if (!id) return true
      return normalizeRawCategories(item).some(rc => `${rc?.id}` === id)
    }

    const selectedItems = items
      .filter(matchesId)
      .sort((a, b) => {
        if (supportVideosCategory) {
          const aOrder = Number.isFinite(a?.order) ? a.order : Number.MAX_SAFE_INTEGER
          const bOrder = Number.isFinite(b?.order) ? b.order : Number.MAX_SAFE_INTEGER
          if (aOrder !== bOrder) return aOrder - bOrder
          return `${a?.title || ''}`.localeCompare(`${b?.title || ''}`, locale, { sensitivity: 'base', numeric: true })
        }
        if (!a?.order) return -b?.order
        if (!b?.order) return -a?.order
        return (a?.order || 0) - (b?.order || 0)
      })
      .map((item) => {
        const favoriteKey = `${item?.uid}:${item?.documentId}`
        const favoriteId = favoritesEnabled ? favoriteDocumentMap[favoriteKey] || null : null

        return {
          ...item,
          isFavorite: !!favoriteId,
          favoriteId,
        }
      })

    const selectedCategoryTitle = (() => {
      if (!id) return ''

      const found = items.find(matchesId)
      if (!found?.raw_category) return routeCategory?.title || ''

      const rawArray = Array.isArray(found.raw_category) ? found.raw_category : [found.raw_category]
      const rc = rawArray.find(cat => `${cat.id}` === `${id}`)

      if (supportVideosCategory) return t('support_videos')
      return rc?.title || routeCategory?.title || ''
    })()
    const selectedSubCategoryTitle = subId
      ? selectedItems
        .flatMap((item) => normalizeSubCategories(item))
        .find((subCategory) => `${subCategory?.id}` === `${subId}`)?.title || ''
      : ''
    const titleParts = [t(type), selectedCategoryTitle, selectedSubCategoryTitle].filter(Boolean)
    const rootPath = `/sub-category/${type}`
    const categoryPath = id ? `/sub-category/${id}/${type}` : rootPath
    const breadcrumbs = [
      { label: t('home'), icon: 'home', to: '/dashboard' },
      { label: t(type), to: selectedCategoryTitle ? rootPath : null, primary: true },
      selectedCategoryTitle ? { label: selectedCategoryTitle, to: selectedSubCategoryTitle ? categoryPath : null } : null,
      selectedSubCategoryTitle ? { label: selectedSubCategoryTitle, to: null } : null,
    ].filter(Boolean)

    return [
      {
        section: ['marketing-materials', 'service-supports', 'sales-support-materials'].includes(type) ? null : t(`${type}_available_for_you`),
        title: titleParts.length > 1 ? `${titleParts[0]} <small>> ${titleParts.slice(1).join(' > ')}</small>` : titleParts[0],
        breadcrumbs,
        data: selectedItems,
        categoryId: id,
        supportVideos: supportVideosCategory,
      }
    ]
  }, [courseViewModel, favoriteDocumentMap, favoritesEnabled, id, isModularType, locale, registers, routeCategory, subId, supportVideosCategory, t, type])

  const selectedCategoryRenderMode = useMemo(() => {
    if (isModularType) {
      return DEFAULT_RENDER_OPTION
    }

    if (!id) return null

    for (const item of registers || []) {
      const matchingCategory = normalizeRawCategories(item).find((category) => `${category?.id}` === `${id}`)

      if (matchingCategory?.render_mode) {
        return matchingCategory.render_mode
      }
    }

    return routeCategory?.render_mode || null
  }, [id, isModularType, registers, routeCategory?.render_mode])

  useEffect(() => {
    setRenderOption(selectedCategoryRenderMode || DEFAULT_RENDER_OPTION)
  }, [selectedCategoryRenderMode, id, type])

  const handleToggleFavorite = async (item) => {
    if (!favoritesEnabled) return
    if (!item?.uid || !item?.documentId) return

    const favoriteKey = `${item.uid}:${item.documentId}`
    const previousFavoriteId = favoriteDocumentMap[favoriteKey] || null
    const nextActive = !previousFavoriteId

    setFavoriteLoadingId(item.documentId)
    setFavoriteDocumentMap((prev) => {
      const next = { ...prev }

      if (nextActive) {
        next[favoriteKey] = 'pending'
      } else {
        delete next[favoriteKey]
      }

      return next
    })

    const result = await toggleFavorite({
      data: {
        target_uid: item.uid,
        target_document_id: item.documentId,
        content_type: item.type,
      },
    })

    if (exposeStrapiError(result)) {
      setFavoriteDocumentMap((prev) => {
        const next = { ...prev }

        if (previousFavoriteId) {
          next[favoriteKey] = previousFavoriteId
        } else {
          delete next[favoriteKey]
        }

        return next
      })
      setFavoriteLoadingId(null)
      return
    }

    setFavoriteDocumentMap((prev) => {
      const next = { ...prev }

      if (result?.data?.active) {
        next[favoriteKey] = result?.data?.favorite?.id || result?.data?.id || 'pending'
      } else {
        delete next[favoriteKey]
      }

      return next
    })
    setFavoriteLoadingId(null)
  }

  const onPageChange = (e, p) => setPage(p)
  return (
    <>
      <FormSpacer extraLarge />
      {loading ? (
        <CatalogSkeleton />
      ) : (
        <>
          {categories?.map((data, index) => (
            <VideoList key={`${type}-${data?.type || 'group'}-${id || 'all'}-${index}`} data={data} wide label={t('filters')} noShare={isModularType || (type === 'sales-support-materials' && !total)} icon={<Icon icon='funnel' />} nobutton filter action={() => setIsActive(true)} renderOption={renderOption} setRenderOption={setRenderOption} listOption enablePdfPreview onToggleFavorite={favoritesEnabled ? handleToggleFavorite : null} favoriteLoading={favoriteLoadingId} error={(data?.supportVideos || type === 'sales-support-materials') && error} retry={retry} emptyText={data?.supportVideos ? t('support_videos_empty') : type === 'sales-support-materials' ? t('sales_support_materials_empty') : null} errorText={t(type === 'sales-support-materials' ? 'sales_support_materials_error' : 'support_videos_error')} errorTitle={type === 'sales-support-materials' ? t('sales_support_materials_unavailable') : null} />))}

          {pageCount <= 1 ? null : (
            <PaginationContainer>
              <CustomTablePagination count={total} page={page} rowsPerPage={pageSize} onPageChange={onPageChange} />
            </PaginationContainer>
          )}
        </>
      )}
    </>
  )
}
